import { useState, useEffect, useCallback, useMemo } from "react";
import { QRCodeCanvas } from "qrcode.react";
import {
  X, CalendarClock, Plus, Trash2, ChevronLeft, ChevronRight, Loader2, MapPin,
  Check, Play, Pencil, List, CalendarDays, User, Phone, Zap, TriangleAlert, HardHat, Wrench, Navigation, Bell, BellOff, FileText, BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError, dlToken } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import ScheduleToday from "@/components/ScheduleToday";
import SpliceMap from "@/components/SpliceMap";

const TYPE_META = {
  awaria: { label: "Awaria", icon: TriangleAlert, cls: "bg-red-500/15 text-red-300 border-red-500/50", dot: "bg-red-500" },
  montaz: { label: "Montaż", icon: HardHat, cls: "bg-cyan-500/15 text-cyan-300 border-cyan-500/50", dot: "bg-cyan-500" },
  prace: { label: "Prace na sieci", icon: Wrench, cls: "bg-amber-500/15 text-amber-300 border-amber-500/50", dot: "bg-amber-500" },
};
const STATUS_META = {
  zaplanowane: { label: "Zaplanowane", cls: "bg-slate-500/15 text-slate-300 border-slate-500/50" },
  podjete: { label: "Zajęty (w toku)", cls: "bg-yellow-500/15 text-yellow-300 border-yellow-500/50" },
  gotowe: { label: "Gotowe", cls: "bg-green-500/15 text-green-300 border-green-500/50" },
};
const PRIORITIES = ["niski", "średni", "wysoki", "krytyczny"];
const pad = (n) => String(n).padStart(2, "0");
const fmtDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const MONTHS = ["Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec", "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"];
const DOW = ["Pon", "Wt", "Śr", "Czw", "Pt", "Sob", "Nd"];

// Dostępność montera w danym dniu: absent (twarda blokada) / offday (ostrzeżenie) / ok.
function monterAvail(m, dateStr) {
  if (!dateStr) return { code: "ok", label: "" };
  const ab = (m.absences || []).find((a) => a.from <= dateStr && a.to >= dateStr);
  if (ab) {
    const lbl = { urlop: "Urlop", l4: "L4", inne: "Nieobecny" }[ab.type] || ab.type;
    return { code: "absent", label: `${lbl} do ${ab.to}` };
  }
  const wd = m.work_days;
  if (wd && wd.length) {
    const dow = (new Date(dateStr).getDay() + 6) % 7; // 0=Pon
    if (!wd.includes(dow)) return { code: "offday", label: "Nie pracuje w tym dniu" };
  }
  return { code: "ok", label: "Dostępny" };
}

const EMPTY = {
  type: "montaz", title: "", description: "", priority: "średni",
  scheduled_date: fmtDate(new Date()), scheduled_time: "09:00", duration_min: 60,
  assignee_ids: [], fault_id: "", client_id: "", address: "",
  notify_sms: false, notify_email: false,
  work_plan: { splices: [], cable: null, splitters: [] },
};

// Edytor planu prac sieciowych: precyzyjne spawy (włókno↔włókno) + montaż kabla po trasie.
const SPLIT_OPTIONS = [
  { label: "1:2", split_type: "1:2", split_ratio: "" },
  { label: "1:4", split_type: "1:4", split_ratio: "" },
  { label: "1:8", split_type: "1:8", split_ratio: "" },
  { label: "1:16", split_type: "1:16", split_ratio: "" },
  { label: "1:32", split_type: "1:32", split_ratio: "" },
  { label: "Asym. 50/50", split_type: "1:2", split_ratio: "50/50" },
  { label: "Asym. 30/70", split_type: "1:2", split_ratio: "30/70" },
  { label: "Asym. 20/80", split_type: "1:2", split_ratio: "20/80" },
  { label: "Asym. 10/90", split_type: "1:2", split_ratio: "10/90" },
  { label: "Asym. 5/95", split_type: "1:2", split_ratio: "5/95" },
];
function WorkPlanEditor({ form, setForm, onDrawRoute }) {
  const { t } = useI18n();
  const [closures, setClosures] = useState([]);
  const [cid, setCid] = useState("");
  const wp = form.work_plan || { splices: [], cable: null };
  useEffect(() => { api.get("/closures").then((r) => setClosures(r.data || [])).catch(() => {}); }, []);
  const closureName = (id) => { const c = closures.find((x) => x.id === id) || {}; return c.number || c.name || "?"; };
  const inp = "w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm outline-none focus:ring-1 focus:ring-cyan-500";
  const plannedFor = (id) => (wp.splices || []).filter((s) => s.closure_id === id);
  const addPlanned = (sp) => {
    setForm((f) => {
      const arr = [...((f.work_plan?.splices) || [])];
      const dup = arr.some((x) => x.closure_id === cid && x.cable_in_id === sp.cable_in_id && x.fiber_in === sp.fiber_in && x.cable_out_id === sp.cable_out_id && x.fiber_out === sp.fiber_out);
      if (dup) { toast.info(t("Ten spaw jest już w planie")); return f; }
      const full = { closure_id: cid, closure_number: closureName(cid), ...sp };
      return { ...f, work_plan: { ...(f.work_plan || { splices: [], cable: null }), splices: [...arr, full] } };
    });
  };
  const removePlanned = (id, filteredIdx) => {
    setForm((f) => {
      const arr = [...((f.work_plan?.splices) || [])];
      const forClosure = arr.map((s, i) => ({ s, i })).filter(({ s }) => s.closure_id === id);
      const target = forClosure[filteredIdx];
      if (!target) return f;
      arr.splice(target.i, 1);
      return { ...f, work_plan: { ...(f.work_plan || {}), splices: arr } };
    });
  };
  const removeSplice = (i) => setForm((f) => ({ ...f, work_plan: { ...(f.work_plan || {}), splices: (f.work_plan?.splices || []).filter((_, k) => k !== i) } }));
  const [feedCables, setFeedCables] = useState([]);
  const [splOpt, setSplOpt] = useState(2);
  const [splCableId, setSplCableId] = useState("");
  const [splFiber, setSplFiber] = useState("");
  useEffect(() => {
    if (cid) api.get(`/closures/${cid}/cables`).then((r) => setFeedCables([...(r.data.cables || []), ...(r.data.nearby || [])])).catch(() => setFeedCables([]));
    else setFeedCables([]);
    setSplCableId(""); setSplFiber("");
  }, [cid]);
  const addSplitter = () => {
    const opt = SPLIT_OPTIONS[Number(splOpt)] || SPLIT_OPTIONS[2];
    const entry = { closure_id: cid, closure_number: closureName(cid), split_type: opt.split_type, split_ratio: opt.split_ratio,
      input_cable_id: splCableId || null, input_cable_number: splCableId ? ((feedCables.find((c) => c.id === splCableId) || {}).number || "") : "",
      input_fiber: splFiber ? Number(splFiber) : null };
    setForm((f) => ({ ...f, work_plan: { ...(f.work_plan || { splices: [], cable: null, splitters: [] }), splitters: [...((f.work_plan?.splitters) || []), entry] } }));
    setSplFiber("");
  };
  const removeSplitter = (i) => setForm((f) => ({ ...f, work_plan: { ...(f.work_plan || {}), splitters: (f.work_plan?.splitters || []).filter((_, k) => k !== i) } }));
  const setCable = (patch) => setForm((f) => ({ ...f, work_plan: { ...(f.work_plan || { splices: [], cable: null }), cable: { ...(f.work_plan?.cable || { cable_type: "", fiber_count: 12, name: "", geometry: null, length_m: 0 }), ...patch } } }));
  const clearCable = () => setForm((f) => ({ ...f, work_plan: { ...(f.work_plan || {}), cable: null } }));
  return (
    <div className="border border-amber-700/40 rounded-lg p-3 space-y-3 bg-amber-500/5" data-testid="work-plan-editor">
      <div className="text-xs font-semibold text-amber-300">{t("Plan prac sieciowych (dla montera)")}</div>
      <div className="space-y-2">
        <div className="text-[11px] text-slate-400">{t("Spawy — kliknij dwa włókna na rozpływie, aby zaplanować (dla dowolnej liczby muf)")}</div>
        <select value={cid} onChange={(e) => setCid(e.target.value)} data-testid="wp-closure" className={inp}>
          <option value="">{t("Wybierz mufę…")}</option>
          {closures.map((c) => <option key={c.id} value={c.id}>{c.number || c.name || c.id.slice(0, 6)}</option>)}
        </select>
        {cid && (
          <div className="bg-slate-900/40 rounded border border-slate-700 p-2" data-testid="wp-splice-map">
            <SpliceMap closureId={cid} closureNumber={closureName(cid)} planMode
              planned={plannedFor(cid)} onPlanAdd={addPlanned} onPlanRemove={(idx) => removePlanned(cid, idx)} />
          </div>
        )}
        {(wp.splices || []).length > 0 && (
          <div className="space-y-1 max-h-40 overflow-y-auto" data-testid="wp-splice-list">
            {(wp.splices || []).map((s, i) => (
              <div key={i} className="flex items-center gap-2 bg-slate-900/60 rounded px-2 py-1 text-xs font-mono" data-testid={`wp-splice-${i}`}>
                <span className="text-amber-300">{s.closure_number}</span>
                <span className="text-slate-300">{s.cable_in_number}/{s.fiber_in}</span>
                <span className="text-slate-500">↔</span>
                <span className="text-slate-300">{s.cable_out_number}/{s.fiber_out}</span>
                <button type="button" onClick={() => removeSplice(i)} data-testid={`wp-splice-del-${i}`} className="ml-auto text-red-300 hover:text-red-200"><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
      {cid && (
        <div className="border-t border-slate-700/60 pt-2 space-y-2" data-testid="wp-splitter-section">
          <div className="text-[11px] text-slate-400">{t("Splitter w mufie (monter zainstaluje, doda się po „Zakończ”)")}</div>
          <div className="grid grid-cols-2 gap-2">
            <select value={splOpt} onChange={(e) => setSplOpt(e.target.value)} data-testid="wp-splitter-type" className={inp}>
              {SPLIT_OPTIONS.map((o, i) => <option key={i} value={i}>{o.label}</option>)}
            </select>
            <select value={splCableId} onChange={(e) => setSplCableId(e.target.value)} data-testid="wp-splitter-cable" className={inp}>
              <option value="">{t("Włókno zasilające — kabel…")}</option>
              {feedCables.map((c) => <option key={c.id} value={c.id}>{c.number}</option>)}
            </select>
            <input type="number" min="1" value={splFiber} onChange={(e) => setSplFiber(e.target.value)} disabled={!splCableId} data-testid="wp-splitter-fiber" placeholder={t("Nr włókna zasilającego")} className={`${inp} col-span-2 disabled:opacity-40`} />
          </div>
          <button type="button" onClick={addSplitter} data-testid="wp-add-splitter" className="w-full bg-fuchsia-600 hover:bg-fuchsia-500 rounded px-2 py-1.5 text-sm flex items-center justify-center gap-1.5"><Plus size={14} /> {t("Dodaj splitter do planu")}</button>
          {(wp.splitters || []).length > 0 && (
            <div className="space-y-1" data-testid="wp-splitter-list">
              {(wp.splitters || []).map((s, i) => (
                <div key={i} className="flex items-center gap-2 bg-slate-900/60 rounded px-2 py-1 text-xs" data-testid={`wp-splitter-${i}`}>
                  <span className="text-fuchsia-300 font-semibold">{s.split_type}{s.split_ratio ? ` (${s.split_ratio})` : ""}</span>
                  <span className="text-amber-300">{s.closure_number}</span>
                  {s.input_cable_id && <span className="text-slate-400 font-mono">← {s.input_cable_number}/{s.input_fiber}</span>}
                  <button type="button" onClick={() => removeSplitter(i)} data-testid={`wp-splitter-del-${i}`} className="ml-auto text-red-300 hover:text-red-200"><Trash2 size={13} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="border-t border-slate-700/60 pt-2 space-y-2">
        <div className="text-[11px] text-slate-400">{t("Montaż kabla po trasie (opcjonalnie)")}</div>
        <div className="grid grid-cols-3 gap-2">
          <input value={wp.cable?.name || ""} onChange={(e) => setCable({ name: e.target.value })} data-testid="wp-cable-name" placeholder={t("Nazwa/nr")} className={inp} />
          <input value={wp.cable?.cable_type || ""} onChange={(e) => setCable({ cable_type: e.target.value })} data-testid="wp-cable-type" placeholder={t("Typ")} className={inp} />
          <input type="number" min="1" value={wp.cable?.fiber_count ?? 12} onChange={(e) => setCable({ fiber_count: Number(e.target.value) || 12 })} data-testid="wp-cable-fibers" placeholder={t("Włókna")} className={inp} />
        </div>
        {wp.cable?.geometry?.length > 1
          ? <div className="flex items-center gap-2 text-xs text-cyan-300"><MapPin size={13} /> {t("Trasa wytyczona")}: {wp.cable.geometry.length} {t("pkt")} · ~{wp.cable.length_m} m
              <button type="button" onClick={clearCable} data-testid="wp-cable-clear" className="ml-auto text-red-300 underline">{t("Usuń trasę")}</button></div>
          : <button type="button" onClick={onDrawRoute} data-testid="wp-draw-route" className="w-full bg-slate-700 hover:bg-slate-600 rounded px-2 py-1.5 text-sm flex items-center justify-center gap-1.5"><MapPin size={14} /> {t("Wytycz trasę na mapie")}</button>}
      </div>
    </div>
  );
}

export default function SchedulePanel({ role, user, onClose, onLocate, onPlanRoute, routeGeo, onRouteConsumed }) {
  const { t } = useI18n();
  const isManager = role === "admin" || role === "projektant";
  const isMonter = role === "monter";

  const [tasks, setTasks] = useState([]);
  const [monters, setMonters] = useState([]);
  const [clients, setClients] = useState([]);
  const [faults, setFaults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("calendar");
  const [cursor, setCursor] = useState(new Date());
  const [filterMonter, setFilterMonter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [daySummary, setDaySummary] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (isManager && filterMonter !== "all") params.assignee_id = filterMonter;
      const { data } = await api.get("/schedule/tasks", { params });
      setTasks(data || []);
    } finally { setLoading(false); }
  }, [isManager, filterMonter]);

  useEffect(() => { load(); }, [load]);
  const [absences, setAbsences] = useState([]);
  useEffect(() => { api.get("/schedule/absences").then((r) => setAbsences(r.data || [])).catch(() => {}); }, []);
  // Po powrocie z rysowania trasy na mapie: odtwórz formularz zadania z localStorage.
  useEffect(() => {
    try {
      const raw = localStorage.getItem("fp_task_form_draft");
      if (raw) {
        const { form: f, editId: eid } = JSON.parse(raw);
        if (f) { setForm(f); setEditId(eid || null); setFormOpen(true); }
        localStorage.removeItem("fp_task_form_draft");
      }
    } catch { /* noop */ }
  }, []);
  // Wstrzyknięcie narysowanej geometrii trasy do planu kabla.
  useEffect(() => {
    if (routeGeo && routeGeo.geometry) {
      setForm((f) => ({ ...f, work_plan: { ...(f.work_plan || { splices: [], cable: null }),
        cable: { ...(f.work_plan?.cable || { cable_type: "", fiber_count: 12, name: "" }),
          geometry: routeGeo.geometry, length_m: routeGeo.length } } }));
      setFormOpen(true);
      onRouteConsumed && onRouteConsumed();
    }
  }, [routeGeo]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isManager) {
      api.get("/schedule/monters").then((r) => setMonters(r.data || [])).catch(() => {});
      api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
      api.get("/faults").then((r) => setFaults(r.data || [])).catch(() => {});
    }
  }, [isManager]);

  const openDetail = async (task) => {
    setSelected(task); setDetail(null);
    try { const { data } = await api.get(`/schedule/tasks/${task.id}`); setDetail(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doAction = async (id, action) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/schedule/tasks/${id}/${action}`);
      setDetail(data); setSelected(data);
      toast.success(action === "accept" ? t("Podjęto zadanie") : t("Zadanie zakończone"));
      load();
      if (isManager) api.get("/schedule/monters").then((r) => setMonters(r.data || [])).catch(() => {});
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm(t("Usunąć to zadanie?"))) return;
    try { await api.delete(`/schedule/tasks/${id}`); toast.success(t("Usunięto")); setSelected(null); setDetail(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openCreate = (date) => {
    setEditId(null);
    setForm({ ...EMPTY, scheduled_date: date || fmtDate(new Date()) });
    setFormOpen(true);
  };
  const openEdit = (task) => {
    setEditId(task.id);
    setForm({
      type: task.type, title: task.title || "", description: task.description || "",
      priority: task.priority || "średni", scheduled_date: task.scheduled_date || fmtDate(new Date()),
      scheduled_time: task.scheduled_time || "", duration_min: task.duration_min || 60,
      assignee_ids: task.assignee_ids || (task.assignee_id ? [task.assignee_id] : []), fault_id: task.fault_id || "", client_id: task.client_id || "",
      address: task.address || "", notify_sms: !!task.notify_sms, notify_email: !!task.notify_email,
      work_plan: task.work_plan || { splices: [], cable: null, splitters: [] },
    });
    setFormOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error(t("Podaj tytuł zadania"));
    setBusy(true);
    try {
      if (editId) await api.put(`/schedule/tasks/${editId}`, form);
      else await api.post("/schedule/tasks", form);
      toast.success(t("Zapisano zadanie"));
      setFormOpen(false); load();
      if (isManager) api.get("/schedule/monters").then((r) => setMonters(r.data || [])).catch(() => {});
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Zapamiętaj formularz i przejdź do rysowania trasy na mapie (poza panelem).
  const drawRouteOnMap = () => {
    try { localStorage.setItem("fp_task_form_draft", JSON.stringify({ form, editId })); } catch { /* noop */ }
    setFormOpen(false);
    onPlanRoute && onPlanRoute();
  };

  // Calendar grid for current month
  const weeks = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = new Date(first);
    const shift = (first.getDay() + 6) % 7; // Monday-first
    start.setDate(first.getDate() - shift);
    const cells = [];
    for (let i = 0; i < 42; i++) { const d = new Date(start); d.setDate(start.getDate() + i); cells.push(d); }
    const w = []; for (let i = 0; i < 6; i++) w.push(cells.slice(i * 7, i * 7 + 7));
    return w;
  }, [cursor]);

  const tasksByDate = useMemo(() => {
    const m = {}; tasks.forEach((tk) => { (m[tk.scheduled_date] = m[tk.scheduled_date] || []).push(tk); }); return m;
  }, [tasks]);

  const input = "w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm outline-none focus:ring-1 focus:ring-cyan-500";
  const todayStr = fmtDate(new Date());

  return (
    <div className="absolute inset-0 bg-slate-900 flex flex-col" data-testid="schedule-panel">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
        <div className="flex items-center gap-2"><CalendarClock size={18} className="text-cyan-400" />
          <h2 className="font-heading font-bold text-lg">{t("Terminarz zleceń")}</h2>
          {isMonter && <span className="text-xs text-slate-400 ml-2">({t("Twoje zadania")})</span>}
        </div>
        <button onClick={onClose} data-testid="schedule-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap px-5 py-2.5 border-b border-slate-800">
        <div className="flex rounded-md overflow-hidden border border-slate-700">
          <button onClick={() => setView("calendar")} data-testid="schedule-view-calendar"
            className={`px-3 py-1.5 text-sm flex items-center gap-1.5 ${view === "calendar" ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800"}`}><CalendarDays size={15} /> {t("Kalendarz")}</button>
          <button onClick={() => setView("list")} data-testid="schedule-view-list"
            className={`px-3 py-1.5 text-sm flex items-center gap-1.5 ${view === "list" ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800"}`}><List size={15} /> {t("Lista")}</button>
          <button onClick={() => setView("today")} data-testid="schedule-view-today"
            className={`px-3 py-1.5 text-sm flex items-center gap-1.5 ${view === "today" ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800"}`}><Navigation size={15} /> {t("Dziś / Trasa")}</button>
        </div>
        {view === "calendar" && (
          <div className="flex items-center gap-1.5">
            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="schedule-prev-month" className="p-1.5 rounded hover:bg-slate-800 text-slate-300"><ChevronLeft size={18} /></button>
            <span className="font-heading font-bold text-sm w-40 text-center">{t(MONTHS[cursor.getMonth()])} {cursor.getFullYear()}</span>
            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="schedule-next-month" className="p-1.5 rounded hover:bg-slate-800 text-slate-300"><ChevronRight size={18} /></button>
            <button onClick={() => setCursor(new Date())} className="text-xs px-2 py-1 rounded border border-slate-700 hover:bg-slate-800 text-slate-300">{t("Dziś")}</button>
          </div>
        )}
        {isManager && (
          <select value={filterMonter} onChange={(e) => setFilterMonter(e.target.value)} data-testid="schedule-filter-monter"
            className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm ml-auto">
            <option value="all">{t("Wszyscy monterzy")}</option>
            {monters.map((m) => <option key={m.id} value={m.id}>{m.name}{m.busy ? ` (${t("zajęty")})` : ""}</option>)}
          </select>
        )}
        {isManager && (
          <button onClick={async () => { try { const { data } = await api.get(`/schedule/monters/day-summary?date=${todayStr}`); setDaySummary(data); } catch (e) { toast.error(t("Nie udało się pobrać podsumowania.")); } }}
            data-testid="schedule-day-summary-btn"
            className="bg-slate-800 border border-slate-700 hover:bg-slate-700 rounded px-3 py-1.5 text-sm flex items-center gap-1.5"><BarChart3 size={16} /> {t("Podsumowanie dnia")}</button>
        )}
        {isManager && (
          <button onClick={() => openCreate()} data-testid="schedule-new-task"
            className={`${isManager && "ml-2"} bg-blue-600 hover:bg-blue-500 rounded px-3 py-1.5 text-sm flex items-center gap-1.5`}><Plus size={16} /> {t("Nowe zadanie")}</button>
        )}
      </div>

      {daySummary && (
        <div className="fixed inset-0 z-[900] bg-black/60 flex items-center justify-center p-4" onClick={() => setDaySummary(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="day-summary-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700 sticky top-0 bg-slate-900">
              <h3 className="font-heading font-bold flex items-center gap-2"><BarChart3 size={18} className="text-cyan-400" /> {t("Podsumowanie dnia")} · {daySummary.date}</h3>
              <button onClick={() => setDaySummary(null)} className="text-slate-400 hover:text-white"><X size={18} /></button>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="text-slate-400 text-xs border-b border-slate-800">
                <th className="text-left px-4 py-2">{t("Monter")}</th>
                <th className="text-center px-2 py-2">{t("Zakończone")}</th>
                <th className="text-right px-4 py-2">{t("Przejechane")}</th>
              </tr></thead>
              <tbody>
                {daySummary.monters.map((m) => (
                  <tr key={m.id} className="border-b border-slate-800" data-testid={`day-summary-${m.id}`}>
                    <td className="px-4 py-2"><span className="flex items-center gap-2"><span className={`w-1.5 h-1.5 rounded-full ${m.online ? "bg-green-400" : "bg-orange-400"}`} />{m.name}</span></td>
                    <td className="text-center px-2 py-2 font-mono">{m.completed}</td>
                    <td className="text-right px-4 py-2 font-mono">{m.distance_km} km</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-slate-500 px-4 py-3">{t("Trasa liczona z pozycji GPS wysłanych przez aplikację montera w godzinach pracy.")}</p>
          </div>
          {(() => {
            const today = new Date().toISOString().slice(0, 10);
            const up = absences.filter((a) => (a.to || a.from) >= today).sort((a, b) => (a.from || "").localeCompare(b.from || ""));
            const LBL = { urlop: "Urlop", l4: "L4", delegacja: "Delegacja", inne: "Inne" };
            return (
              <div className="bg-slate-800/40 rounded-xl border border-slate-700 overflow-hidden" data-testid="schedule-absences">
                <div className="px-4 py-2 text-sm font-semibold text-slate-200 border-b border-slate-800 flex items-center gap-2">{t("Nieobecności")} <span className="text-xs text-slate-500">({up.length})</span></div>
                {up.length === 0 ? <p className="text-[12px] text-slate-500 px-4 py-3">{t("Brak zgłoszonych nieobecności.")}</p> : (
                  <div className="divide-y divide-slate-800 max-h-64 overflow-y-auto">
                    {up.map((a) => (
                      <div key={a.id} className="px-4 py-2 flex items-center justify-between text-xs" data-testid={`absence-${a.id}`}>
                        <div className="min-w-0"><div className="text-slate-200 truncate">{a.user_name || "—"}<span className="ml-1.5 text-[10px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-300">{LBL[a.type] || a.type}</span></div>{a.reason && <div className="text-[10px] text-slate-500 truncate">{a.reason}</div>}</div>
                        <div className="text-slate-400 font-mono whitespace-nowrap ml-2">{a.from} → {a.to}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Monter busy strip */}
      {isManager && monters.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap px-5 py-2 border-b border-slate-800 text-xs" data-testid="schedule-monter-strip">
          {monters.map((m) => {
            const av = monterAvail(m, todayStr);
            const cls = av.code === "absent"
              ? "bg-red-500/15 text-red-300 border-red-500/50"
              : m.in_field ? "bg-yellow-500/15 text-yellow-300 border-yellow-500/50" : "bg-green-500/15 text-green-300 border-green-500/50";
            const txt = av.code === "absent" ? av.label : m.in_field ? t("W terenie") : t("Wolny");
            return (
              <span key={m.id} className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 ${cls}`} data-testid={`monter-status-${m.id}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${m.online ? "bg-green-400" : "bg-slate-500"}`} title={m.online ? t("Aktywny w aplikacji") : (m.last_seen ? `${t("Ostatnio aktywny")}: ${new Date(m.last_seen).toLocaleString("pl-PL")}` : t("Nigdy nie zalogowany"))} />
                <HardHat size={12} /> {m.name} · {txt}
                {!m.online && <span className="text-slate-400">· offline</span>}
                {m.push_enabled
                  ? <Bell size={11} className="text-cyan-400" title={t("Powiadomienia push włączone")} />
                  : <BellOff size={11} className="text-slate-500" title={t("Brak powiadomień push")} />}
              </span>
            );
          })}
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="animate-spin mr-2" /> {t("Ładowanie…")}</div>
        ) : view === "calendar" ? (
          <div className="min-w-[720px]">
            <div className="grid grid-cols-7 gap-1 mb-1">
              {DOW.map((d) => <div key={d} className="text-center text-xs font-semibold text-slate-400 py-1">{t(d)}</div>)}
            </div>
            {weeks.map((wk, wi) => (
              <div key={wi} className="grid grid-cols-7 gap-1 mb-1">
                {wk.map((d) => {
                  const ds = fmtDate(d);
                  const inMonth = d.getMonth() === cursor.getMonth();
                  const dayTasks = tasksByDate[ds] || [];
                  return (
                    <div key={ds} data-testid={`schedule-day-${ds}`}
                      className={`min-h-[92px] rounded-md border p-1 ${inMonth ? "border-slate-700 bg-slate-800/40" : "border-slate-800 bg-slate-900/40 opacity-50"} ${ds === todayStr ? "ring-1 ring-cyan-500" : ""}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs ${ds === todayStr ? "text-cyan-400 font-bold" : "text-slate-400"}`}>{d.getDate()}</span>
                        {isManager && inMonth && <button onClick={() => openCreate(ds)} className="text-slate-500 hover:text-cyan-400" title={t("Dodaj zadanie")}><Plus size={13} /></button>}
                      </div>
                      <div className="space-y-1">
                        {dayTasks.slice(0, 4).map((tk) => {
                          const meta = TYPE_META[tk.type] || TYPE_META.montaz;
                          return (
                            <button key={tk.id} onClick={() => openDetail(tk)} data-testid={`schedule-chip-${tk.id}`}
                              className={`w-full text-left text-[11px] rounded px-1.5 py-0.5 flex items-center gap-1 border ${meta.cls} ${tk.status === "gotowe" ? "opacity-60 line-through" : ""}`}>
                              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${meta.dot}`} />
                              <span className="truncate">{tk.scheduled_time ? tk.scheduled_time + " " : ""}{tk.title}</span>
                            </button>
                          );
                        })}
                        {dayTasks.length > 4 && <span className="text-[10px] text-slate-500">+{dayTasks.length - 4} {t("więcej")}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        ) : view === "today" ? (
          <ScheduleToday role={role} user={user} isManager={isManager}
            monters={monters} filterMonter={filterMonter} onReload={load} onLocate={onLocate} />
        ) : (
          <div className="space-y-2 max-w-4xl">
            {tasks.length === 0 && <div className="text-sm text-slate-500 py-10 text-center">{t("Brak zaplanowanych zadań.")}</div>}
            {tasks.map((tk) => {
              const meta = TYPE_META[tk.type] || TYPE_META.montaz;
              const sm = STATUS_META[tk.status] || STATUS_META.zaplanowane;
              const Icon = meta.icon;
              return (
                <button key={tk.id} onClick={() => openDetail(tk)} data-testid={`schedule-row-${tk.id}`}
                  className="w-full text-left bg-slate-800 border border-slate-700 rounded-lg p-3 hover:border-cyan-600 flex items-center gap-3">
                  <Icon size={18} className={meta.cls.split(" ")[1]} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold truncate">{tk.title}</span>
                      <span className={`text-[10px] rounded-full border px-1.5 py-0.5 ${sm.cls}`}>{t(sm.label)}</span>
                    </div>
                    <div className="text-xs text-slate-400 truncate">{tk.task_no} · {t(meta.label)} · {tk.scheduled_date} {tk.scheduled_time} · {tk.assignee_name || t("nieprzypisane")}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="absolute inset-0 z-[850] bg-black/60 flex items-center justify-center p-4" onClick={() => { setSelected(null); setDetail(null); }}>
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="schedule-detail">
            {(() => {
              const tk = detail || selected;
              const meta = TYPE_META[tk.type] || TYPE_META.montaz;
              const sm = STATUS_META[tk.status] || STATUS_META.zaplanowane;
              const ci = detail?.client_info;
              const canAct = isManager || (isMonter && [...(tk.assignee_ids || []), tk.assignee_id].includes(user?.id));
              return (
                <>
                  <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700 sticky top-0 bg-slate-900">
                    <div className="flex items-center gap-2">
                      <span className={`text-[11px] rounded-full border px-2 py-0.5 ${meta.cls}`}>{t(meta.label)}</span>
                      <span className={`text-[11px] rounded-full border px-2 py-0.5 ${sm.cls}`}>{t(sm.label)}</span>
                      <span className="text-xs text-slate-500 font-mono">{tk.task_no}</span>
                    </div>
                    <button onClick={() => { setSelected(null); setDetail(null); }} className="text-slate-400 hover:text-white"><X size={18} /></button>
                  </div>
                  <div className="p-5 space-y-4">
                    <div>
                      <h3 className="font-heading font-bold text-lg">{tk.title}</h3>
                      <p className="text-xs text-slate-400 mt-0.5">{tk.scheduled_date} {tk.scheduled_time} · {t("Czas trwania")}: {tk.duration_min} min · {t("Priorytet")}: {tk.priority}</p>
                      <p className="text-xs text-slate-400">{t("Monter")}: <b className="text-slate-200">{tk.assignee_name || t("nieprzypisane")}</b></p>
                      {tk.address && <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5"><MapPin size={12} /> {tk.address}</p>}
                      {tk.fault_no && <p className="text-xs text-amber-300 mt-0.5">{t("Powiązana awaria")}: {tk.fault_no}</p>}
                    </div>
                    {tk.description && <p className="text-sm text-slate-300 bg-slate-800 rounded-lg p-3 whitespace-pre-wrap">{tk.description}</p>}

                    {(tk.work_plan?.splices?.length > 0 || tk.work_plan?.cable?.geometry) && (
                      <div className="bg-amber-500/5 border border-amber-700/40 rounded-lg p-3 text-sm space-y-1" data-testid="schedule-work-plan">
                        <div className="font-semibold text-amber-300 text-xs flex items-center gap-1.5"><Wrench size={13} /> {t("Plan prac")} {tk.work_plan?.applied ? `· ${t("wykonano")} ✓` : ""}</div>
                        {tk.work_plan?.splices?.length > 0 && <div className="text-xs text-slate-300">{t("Spawy")}: <b className={tk.work_plan.splices.filter((s) => s.done).length === tk.work_plan.splices.length ? "text-green-400" : "text-amber-300"}>{tk.work_plan.splices.filter((s) => s.done).length}/{tk.work_plan.splices.length}</b> {t("wykonane")} ({[...new Set(tk.work_plan.splices.map((s) => s.closure_number))].join(", ")})</div>}
                        {tk.work_plan?.splitters?.length > 0 && <div className="text-xs text-slate-300">{t("Splittery")}: <b className="text-fuchsia-300">{tk.work_plan.splitters.length}</b>{tk.work_plan.applied ? ` · ${t("dodane")} ✓` : ""}</div>}
                        {tk.work_plan?.cable?.geometry && <div className="text-xs text-slate-300">{t("Montaż kabla")}: {tk.work_plan.cable.name || "—"} · {tk.work_plan.cable.fiber_count} wł. · ~{tk.work_plan.cable.length_m} m{tk.work_plan.cable.created_cable_id ? ` · ${t("utworzono")} ✓` : ""}</div>}
                      </div>
                    )}

                    {ci && (
                      <div className="bg-slate-800 border border-slate-700 rounded-lg p-4" data-testid="schedule-client-card">
                        <h4 className="font-heading font-bold text-sm mb-2 flex items-center gap-1.5"><User size={15} className="text-cyan-400" /> {t("Dane klienta")}</h4>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                          <div><span className="text-slate-500">{t("Nazwa")}:</span> {ci.name || "—"}</div>
                          <div><span className="text-slate-500">{t("Umowa")}:</span> {ci.contract_no || "—"}</div>
                          <div className="flex items-center gap-1"><Phone size={12} className="text-slate-500" /> {ci.phone ? <a href={`tel:${ci.phone}`} className="text-cyan-300">{ci.phone}</a> : "—"}</div>
                          <div><span className="text-slate-500">Email:</span> {ci.email || "—"}</div>
                          <div className="col-span-2 flex items-center gap-1"><MapPin size={12} className="text-slate-500" /> {ci.address || "—"}</div>
                          <div><span className="text-slate-500">{t("Technologia")}:</span> {ci.technology || "—"}</div>
                          <div><span className="text-slate-500">OLT:</span> {ci.olt_name || "—"} · {t("Port PON")} {ci.pon_port || "—"}</div>
                          <div><span className="text-slate-500">{t("Splitter")}:</span> {ci.splitter_number || "—"}{ci.splitter_type ? ` (${ci.splitter_type})` : ""} {t("port")} {ci.splitter_port ?? "—"}</div>
                          <div><span className="text-slate-500">{t("Mufa/y")}:</span> {(ci.closures && ci.closures.length) ? ci.closures.join(", ") : "—"}</div>
                          <div className="flex items-center gap-1"><Zap size={12} className="text-yellow-400" /> {t("Moc zmierzona")}: <b>{ci.measured_rx_dbm != null ? `${ci.measured_rx_dbm} dBm` : "—"}</b></div>
                          <div className="flex items-center gap-1"><Zap size={12} className="text-cyan-400" /> {t("Moc szacowana")}: <b>{ci.expected_rx_dbm != null ? `${ci.expected_rx_dbm} dBm` : "—"}</b></div>
                          {ci.onu_sn && <div><span className="text-slate-500">S/N ONU:</span> {ci.onu_sn}</div>}
                          {ci.onu_mac && <div><span className="text-slate-500">MAC:</span> {ci.onu_mac}</div>}
                        </div>
                        {ci.lat != null && onLocate && (
                          <button onClick={() => { onLocate(ci); setSelected(null); setDetail(null); onClose && onClose(); }}
                            data-testid="schedule-locate-client" className="mt-3 text-xs bg-slate-700 hover:bg-slate-600 rounded px-3 py-1.5 flex items-center gap-1.5"><MapPin size={13} /> {t("Pokaż na mapie")}</button>
                        )}
                      </div>
                    )}

                    {/* QR code with worker briefing */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 flex items-center gap-4" data-testid="schedule-qr">
                      <div className="bg-white p-2 rounded">
                        <QRCodeCanvas value={[
                          `Zadanie: ${tk.task_no}`, `Typ: ${meta.label}`, `Tytul: ${tk.title}`,
                          `Termin: ${tk.scheduled_date} ${tk.scheduled_time}`,
                          ci ? `Klient: ${ci.name || ""}` : "", ci ? `Tel: ${ci.phone || ""}` : "",
                          ci ? `Adres: ${ci.address || ""}` : "", ci ? `Splitter: ${ci.splitter_number || ""} port ${ci.splitter_port ?? ""}` : "",
                          ci ? `Mufa: ${(ci.closures || []).join(",")}` : "", ci ? `Moc: ${ci.measured_rx_dbm ?? "?"}/${ci.expected_rx_dbm ?? "?"} dBm` : "",
                        ].filter(Boolean).join("\n")} size={112} />
                      </div>
                      <div className="text-xs text-slate-400">{t("Zeskanuj kod QR, aby otrzymać zlecenie z danymi klienta na telefonie montera.")}</div>
                    </div>

                    <a href={`${API}/schedule/tasks/${tk.id}/protocol.pdf?auth=${encodeURIComponent(dlToken())}`}
                      target="_blank" rel="noreferrer" data-testid="schedule-protocol"
                      className="inline-flex items-center gap-1.5 bg-slate-700 hover:bg-slate-600 rounded px-3 py-2 text-sm w-max">
                      <FileText size={14} /> {t("Protokół odbioru (PDF)")}
                    </a>

                    {/* Actions */}
                    {canAct && (
                      <div className="flex items-center gap-2 flex-wrap">
                        {tk.status === "zaplanowane" && (tk.assignee_id || (tk.assignee_ids && tk.assignee_ids.length > 0)) && (
                          <button onClick={() => doAction(tk.id, "accept")} disabled={busy} data-testid="schedule-accept"
                            className="bg-yellow-600 hover:bg-yellow-500 rounded px-4 py-2 text-sm flex items-center gap-1.5 disabled:opacity-60"><Play size={15} /> {t("Podejmij zlecenie")}</button>
                        )}
                        {tk.status === "podjete" && (
                          <button onClick={() => doAction(tk.id, "complete")} disabled={busy} data-testid="schedule-complete"
                            className="bg-green-600 hover:bg-green-500 rounded px-4 py-2 text-sm flex items-center gap-1.5 disabled:opacity-60"><Check size={15} /> {t("Gotowe")}</button>
                        )}
                        {isManager && (
                          <>
                            <button onClick={() => { setSelected(null); openEdit(tk); }} data-testid="schedule-edit" className="bg-slate-700 hover:bg-slate-600 rounded px-3 py-2 text-sm flex items-center gap-1.5"><Pencil size={14} /> {t("Edytuj")}</button>
                            <button onClick={() => del(tk.id)} data-testid="schedule-delete" className="bg-red-900/40 hover:bg-red-900/60 text-red-300 rounded px-3 py-2 text-sm flex items-center gap-1.5"><Trash2 size={14} /> {t("Usuń")}</button>
                          </>
                        )}
                      </div>
                    )}

                    {tk.events && tk.events.length > 0 && (
                      <div className="text-xs text-slate-500 border-t border-slate-800 pt-2">
                        {tk.events.slice().reverse().map((ev, i) => <div key={i}>{(ev.ts || "").slice(0, 16).replace("T", " ")} · {ev.user} — {ev.text}</div>)}
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Create / Edit form */}
      {formOpen && (
        <div className="absolute inset-0 z-[850] bg-black/60 flex items-center justify-center p-4" onClick={() => setFormOpen(false)}>
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="schedule-form">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700 sticky top-0 bg-slate-900">
              <h3 className="font-heading font-bold">{editId ? t("Edytuj zadanie") : t("Nowe zadanie")}</h3>
              <button onClick={() => setFormOpen(false)} className="text-slate-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-2.5">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">{t("Typ")}</label>
                  <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="schedule-form-type" className={input}>
                    {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{t(v.label)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">{t("Priorytet")}</label>
                  <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="schedule-form-priority" className={input}>
                    {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">{t("Tytuł")} *</label>
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="schedule-form-title" className={input} />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">{t("Opis")}</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="schedule-form-desc" className={input} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">{t("Data")}</label>
                  <input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} data-testid="schedule-form-date" className={input} />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">{t("Godzina")}</label>
                  <input type="time" value={form.scheduled_time} onChange={(e) => setForm({ ...form, scheduled_time: e.target.value })} data-testid="schedule-form-time" className={input} />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">{t("Czas [min]")}</label>
                  <input type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} data-testid="schedule-form-duration" className={input} />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">{t("Przypisz monterów")}</label>
                {(() => {
                  const avail = monters.filter((m) => { const av = monterAvail(m, form.scheduled_date); return av.code === "ok" && !m.busy; });
                  return <div className="text-[11px] text-slate-400 mb-1.5">🟢 {t("Dostępni w tym dniu")}: <span className="text-green-400">{avail.length ? avail.map((m) => m.name).join(", ") : t("brak — sprawdź grafik/nieobecności")}</span></div>;
                })()}
                <div className="max-h-44 overflow-y-auto rounded border border-slate-700 bg-slate-900 divide-y divide-slate-800" data-testid="schedule-form-assignees">
                  {monters.length === 0 && <p className="text-[11px] text-amber-400 p-2">{t("Brak monterów — dodaj konta z rolą „Monter\" w panelu Użytkownicy.")}</p>}
                  {monters.map((m) => {
                    const av = monterAvail(m, form.scheduled_date);
                    const checked = form.assignee_ids.includes(m.id);
                    const blocked = !checked && (av.code === "absent" || m.busy);
                    const tag = av.code === "absent" ? av.label : av.code === "offday" ? "nie pracuje w tym dniu" : m.busy ? t("zajęty") : (m.online ? "online" : "offline");
                    const tagCls = av.code === "absent" ? "text-red-400" : av.code === "offday" ? "text-amber-400" : m.busy ? "text-yellow-400" : m.online ? "text-green-400" : "text-slate-500";
                    return (
                      <label key={m.id} className={`flex items-center gap-2 px-2.5 py-1.5 text-sm ${blocked ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:bg-slate-800"}`}>
                        <input type="checkbox" checked={checked} disabled={blocked} data-testid={`assignee-check-${m.id}`}
                          onChange={(e) => setForm((f) => ({ ...f, assignee_ids: e.target.checked ? [...f.assignee_ids, m.id] : f.assignee_ids.filter((x) => x !== m.id) }))} />
                        <span className="flex-1">{m.name}</span>
                        <span className={`text-[11px] ${tagCls}`}>{tag}</span>
                      </label>
                    );
                  })}
                </div>
                {form.assignee_ids.some((id) => { const m = monters.find((x) => x.id === id); const av = m && monterAvail(m, form.scheduled_date); return av && av.code === "offday"; }) &&
                  <p className="text-[11px] text-amber-400 mt-1">⚠ {t("Któryś monter nie pracuje w wybranym dniu — możesz zlecić, ale sprawdź grafik.")}</p>}
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">{t("Powiąż z awarią (opcjonalnie)")}</label>
                <select value={form.fault_id} onChange={(e) => setForm({ ...form, fault_id: e.target.value })} data-testid="schedule-form-fault" className={input}>
                  <option value="">{t("— brak —")}</option>
                  {faults.map((f) => <option key={f.id} value={f.id}>{f.ticket_no} · {f.title}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">{t("Powiąż z klientem (opcjonalnie)")}</label>
                <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="schedule-form-client" className={input}>
                  <option value="">{t("— brak —")}</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.address ? ` · ${c.address}` : ""}</option>)}
                </select>
                <p className="text-[11px] text-slate-500 mt-1">{t("Po powiązaniu monter zobaczy dane klienta, mufę, splitter i moc sygnału.")}</p>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">{t("Adres (jeśli bez klienta)")}</label>
                <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="schedule-form-address" className={input} />
              </div>
              {(form.type === "montaz" || form.type === "prace") && (
                <WorkPlanEditor form={form} setForm={setForm} onDrawRoute={drawRouteOnMap} />
              )}
              <div className="flex items-center gap-4 pt-1">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.notify_sms} onChange={(e) => setForm({ ...form, notify_sms: e.target.checked })} data-testid="schedule-form-sms" className="accent-cyan-500" /> {t("Powiadom SMS")}
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.notify_email} onChange={(e) => setForm({ ...form, notify_email: e.target.checked })} data-testid="schedule-form-email" className="accent-cyan-500" /> {t("Powiadom e-mail")}
                </label>
              </div>
              <button onClick={save} disabled={busy} data-testid="schedule-form-save"
                className="w-full bg-blue-600 hover:bg-blue-500 rounded py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60 mt-2">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} {t("Zapisz zadanie")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
